import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import {
  createSetReceiptAttachmentIds,
  getCreditCardBulkTemplate,
} from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.actions'
import {
  selectBulkTemplateFromState,
  selectErrorDetailMapFromState,
  selectFilteredRecentUserEmailOptions,
  selectFilteredUserEmailOptions,
  selectMultipleErrorDetailMapFromState,
  selectMultipleTopLevelErrorFromState,
  selectPostingCreditCardBulkVirtualCards,
  selectPostingVirtualCardFromState,
  selectReceiptAttachmentModelsList,
  selectTemplateTypeFromState,
  selectTopLevelErrorFromState,
  selectUploadingAttachmentsFromState,
  selectUsersFullPaginatedData,
  selectVirtualCardPushTasks,
} from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  CreateBillPayVirtualCardToastMessage,
  CreateVirtualCardAndInvitedUserToastMessage,
  CreateVirtualCardPendingFinalApprovalToastMessage,
} from 'src/app/Toast/Toast'
import CreateVirtualCardGuestAccessContent from 'src/components/CreateVirtualCardGuestAccessContent/CreateVirtualCardGuestAccessContent'
import CreateVirtualCardIssuerCycleContent from 'src/components/CreateVirtualCardIssuerCycleContent/CreateVirtualCardIssuerCycleContent'
import CreateVirtualCardMultipleContent from 'src/components/CreateVirtualCardMultipleContent/CreateVirtualCardMultipleContent'
import CreateVirtualCardRecurringContent from 'src/components/CreateVirtualCardRecurringContent/CreateVirtualCardRecurringContent'
import CreateVirtualCardStandardContent from 'src/components/CreateVirtualCardStandardContent/CreateVirtualCardStandardContent'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import {
  ExtendCurrency,
  OrganizationRoles,
  VirtualCardTemplateTypes,
} from 'src/enums'
import VirtualCardTypePicker from 'src/feature/VirtualCardTypePicker/VirtualCardTypePicker'
import { useFlyoutDispatch } from 'src/hooks'
import { useVirtualCardFormStatistics } from 'src/hooks/virtualCardForm'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import {
  useVirtualCardFormCreditCards,
  type VirtualCardFormScreen,
} from 'src/queries/creditCards/domain/useVirtualCardFormCreditCards'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { getCountrys } from 'src/redux/actions/data/countrys.actions'
import { getCreditCard } from 'src/redux/actions/data/creditCards.actions'
import {
  INVITE_ORGANIZATION_SUCCESS,
  postInviteToOrganizationAndOpenToastAndCloseFlyout,
} from 'src/redux/actions/data/organizations.actions'
import { uploadAttachments } from 'src/redux/actions/data/receiptAttachments.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import { postCreditCardBulkVirtualCards } from 'src/redux/actions/data/virtualCardPushTasks.actions'
import {
  POST_VIRTUAL_CARD_SUCCESS,
  postBulkVirtualCardAndOpenToastAndCloseFlyout,
  postVirtualCardAndOpenToastAndCloseFlyout,
} from 'src/redux/actions/data/virtualCards.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCardApprovalsEnabled,
  selectCardApprovalsFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectCreditCardsActiveSourceCountStatisticsFromState } from 'src/redux/selectors/data/statistics.selectors'
import {
  selectUserEmailMap,
  selectUserListModelsFromState,
} from 'src/redux/selectors/data/users.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectLocationFromState,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import {
  selectAppName,
  selectBrandFeaturesFromState,
} from 'src/redux/selectors/session.selectors'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { handlePageLocation } from 'src/utils/analytics.utils'

interface CreateVirtualCardFormProps {
  screen?: CreateVirtualCardScreen
}

const CreateVirtualCardForm: React.FC<CreateVirtualCardFormProps> = (props) => {
  const translate = useTranslate()
  const queryParams = useSelector(selectQueryParams)
  const receiptAttachments = useSelector(selectReceiptAttachmentModelsList)
  const uploadingAttachmentThumbnails = useSelector(
    selectUploadingAttachmentsFromState,
  )
  const postingVirtualCard = useSelector(selectPostingVirtualCardFromState)
  const bulkTemplate = useSelector(selectBulkTemplateFromState)
  const templateType = useSelector(selectTemplateTypeFromState)
  const virtualCardPushTasks = useSelector(selectVirtualCardPushTasks)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const multipleTopLevelError = useSelector(
    selectMultipleTopLevelErrorFromState,
  )
  const multipleErrorDetailMap = useSelector(
    selectMultipleErrorDetailMapFromState,
  )
  const postingCreditCardBulkVirtualCards = useSelector(
    selectPostingCreditCardBulkVirtualCards,
  )
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const filteredUserEmailOptions = useSelector(selectFilteredUserEmailOptions)
  const filteredRecentUserEmailOptions = useSelector(
    selectFilteredRecentUserEmailOptions,
  )
  const userOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const location = useSelector(selectLocationFromState)
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const user = useSelector(selectUserListModelFromState)
  const users = useSelector(selectUserListModelsFromState)
  const userEmailMap = useSelector(selectUserEmailMap)
  const activeRCNs = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const appName = useSelector(selectAppName)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled
  const cardApprovalsEnabled = useSelector(selectCardApprovalsEnabled)
  const cardApprovalRules = useSelector(selectCardApprovalsFromState)

  const isCurrentUserCardApprover = cardApprovalRules?.approverId === user?.id
  const showSoftAlert = cardApprovalsEnabled && !isCurrentUserCardApprover
  const { recurringVirtualCardsEnabled } = useSelector(
    selectBrandFeaturesFromState,
  )

  const dispatch = useFlyoutDispatch()

  const formStats = useVirtualCardFormStatistics({
    variant: 'create',
  })

  const dispatchUploadAttachments = (
    attachments: PostReceiptAttachmentRequest[],
  ): void => {
    dispatch(uploadAttachments(attachments))
  }
  const dispatchPostVirtualCardAndOpenToastAndCloseFlyout = async (
    body: Partial<PostVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ): Promise<void> => {
    const postSilentInvites =
      ((showGuestRole && user !== undefined) || user?.hasOrgAdminPermissions) &&
      user.organizationId !== undefined &&
      body.recipient !== undefined &&
      (userEmailMap[body.recipient] === undefined ||
        user.organizationId !== userEmailMap[body.recipient].organizationId) &&
      !!inviteUsers
    const showCardApproverToast =
      body.currency === ExtendCurrency.Cad
        ? body.balanceCents !== undefined &&
          cardApprovalRules?.thresholds?.cadCents !== undefined &&
          body.balanceCents > cardApprovalRules?.thresholds.cadCents
        : body.balanceCents !== undefined &&
          cardApprovalRules?.thresholds?.usdCents !== undefined &&
          body.balanceCents > cardApprovalRules?.thresholds.usdCents

    let postInviteAction
    if (
      postSilentInvites &&
      user?.organizationId !== undefined &&
      body.recipient
    ) {
      postInviteAction = await dispatch(
        postInviteToOrganizationAndOpenToastAndCloseFlyout(
          user.organizationId,
          {
            emails: [body.recipient],
            role:
              role === OrganizationRoles.guest
                ? OrganizationRoles.guest
                : undefined,
          },
          true,
        ),
      )
    }
    if (
      !postSilentInvites ||
      postInviteAction?.type === INVITE_ORGANIZATION_SUCCESS
    ) {
      const postVirtualCardAction = await dispatch(
        postVirtualCardAndOpenToastAndCloseFlyout(
          body,
          true,
          postSilentInvites,
          showCardApproverToast && !isCurrentUserCardApprover,
        ),
      )
      // Toast message should be handled by postVirtualCardAndOpenToastAndCloseFlyout when no org invite is sent
      if (
        postInviteAction?.type === INVITE_ORGANIZATION_SUCCESS &&
        postVirtualCardAction.type === POST_VIRTUAL_CARD_SUCCESS
      ) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: showCardApproverToast ? (
              <CreateVirtualCardPendingFinalApprovalToastMessage />
            ) : (
              <CreateVirtualCardAndInvitedUserToastMessage
                virtualCard={
                  new VirtualCardListModel(
                    postVirtualCardAction.payload.virtualCard,
                  )
                }
                users={users}
              />
            ),
          }),
        )
      }
    }
  }
  const dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout = async (
    body: Partial<PostBulkVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ): Promise<void> => {
    const selectedUsers = Object.values(userEmailMap).filter((user) =>
      body.recipients?.includes(user.email),
    )
    const newEmails =
      body.recipients?.filter(
        (email) => Object.keys(userEmailMap).indexOf(email) < 0,
      ) ?? []
    const selectedUsersNotInOrg = selectedUsers
      .filter(
        (selectedUser) => selectedUser.organizationId !== user?.organizationId,
      )
      .map((user) => user.email)
      .concat(newEmails)

    const postSilentInvites =
      ((showGuestRole && user !== undefined) || user?.hasOrgAdminPermissions) &&
      user.organizationId !== undefined &&
      selectedUsersNotInOrg.length > 0 &&
      !!inviteUsers
    let postInviteAction
    if (postSilentInvites && user?.organizationId !== undefined) {
      // can update this to use any role instead of a ternary when we want to allow other roles on invite
      const body =
        role === OrganizationRoles.guest
          ? { emails: selectedUsersNotInOrg, role: OrganizationRoles.guest }
          : { emails: selectedUsersNotInOrg }
      postInviteAction = await dispatch(
        postInviteToOrganizationAndOpenToastAndCloseFlyout(
          user.organizationId,
          body,
          true,
        ),
      )
    }
    // send virtual cards after users are successfully invited
    if (
      !postSilentInvites ||
      postInviteAction?.type === INVITE_ORGANIZATION_SUCCESS
    ) {
      dispatch(
        postBulkVirtualCardAndOpenToastAndCloseFlyout(body, postSilentInvites),
      )
    }
  }
  const dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout = async (
    body: Partial<PostVirtualCardRequest>,
  ): Promise<void> => {
    // TODO: handle card approver toast
    // const showCardApproverToast = (body.currency === ExtendCurrency.Cad)
    //   ? body.balanceCents !== undefined && cardApprovalRules?.thresholds?.cadCents !== undefined && body.balanceCents > cardApprovalRules?.thresholds.cadCents
    //   : body.balanceCents !== undefined && cardApprovalRules?.thresholds?.usdCents !== undefined && body.balanceCents > cardApprovalRules?.thresholds.usdCents

    const postVirtualCardAction = await dispatch(
      postVirtualCardAndOpenToastAndCloseFlyout(body, true, true),
    )
    if (postVirtualCardAction.type === POST_VIRTUAL_CARD_SUCCESS) {
      const virtualCard = new VirtualCardListModel(
        postVirtualCardAction.payload.virtualCard,
      )
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <CreateBillPayVirtualCardToastMessage
              virtualCard={virtualCard}
              shared={body.billPayMeta?.vendorEmail !== undefined}
            />
          ),
        }),
      )
    }
  }
  const dispatchGetCreditCardBulkTemplate = (type: string): void => {
    dispatch(getCreditCardBulkTemplate(type))
  }
  const dispatchPostCreditCardBulkVirtualCards = (
    creditCardId: string,
    formData: FormData,
  ): void => {
    dispatch(postCreditCardBulkVirtualCards(creditCardId, formData))
  }

  const deleteAttachment = (attachmentId: string): void => {
    dispatch(
      createSetReceiptAttachmentIds(
        receiptAttachments
          .filter((att) => att.id !== attachmentId)
          .map((att) => att.id),
      ),
    )
  }

  const [screen, setScreen] = useState(
    props.screen ?? location.state?.screen ?? CreateVirtualCardScreen.Pick,
  )
  const hideRecurringVCNOption =
    screen === CreateVirtualCardScreen.Pick && !recurringVirtualCardsEnabled

  const activeFormScreen: VirtualCardFormScreen =
    (screen as string) in
    ({
      Standard: true,
      BillPay: true,
      MultipleBillPay: true,
      Multiple: true,
      Recurring: true,
      GuestAccess: true,
      RecurringGuest: true,
      DecliningBalance: true,
      IssuerCycle: true,
    } satisfies Record<VirtualCardFormScreen, true>)
      ? (screen as VirtualCardFormScreen)
      : 'Standard'

  const formCreditCards = useVirtualCardFormCreditCards(activeFormScreen, {
    billPayEnabled:
      screen === CreateVirtualCardScreen.BillPay ||
      screen === CreateVirtualCardScreen.MultipleBillPay
        ? true
        : undefined,
  })

  const formCreditCardStandardOptions = useMemo(
    () => formCreditCards.standard.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.standard.options],
  )
  const formCreditCardRecurringOptions = useMemo(
    () => formCreditCards.recurring.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.recurring.options],
  )
  const formCreditCardGuestAccessOptions = useMemo(
    () => formCreditCards.guestAccess.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.guestAccess.options],
  )
  const formCreditCardBalanceLockOptions = useMemo(
    () => formCreditCards.balanceLock.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.balanceLock.options],
  )
  const formCreditCardIssuerCycleOptions = useMemo(
    () => formCreditCards.issuerCycle.options.map((cc) => creditCardOption(cc)),
    [formCreditCards.issuerCycle.options],
  )

  useEffect((): void => {
    if (typeof queryParams.creditCardId === 'string') {
      dispatch(getCreditCard(queryParams['creditCardId']))
    }
  }, [queryParams['creditCardId']])

  useEffect((): void => {
    if (bulkTemplate !== undefined) {
      const pom = document.createElement('a')
      document.body.appendChild(pom)
      const blob = new Blob([bulkTemplate], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      pom.href = window.URL.createObjectURL(blob)
      pom.download =
        'Virtual Card Template - ' +
        (templateType === VirtualCardTemplateTypes.Recurring
          ? 'RECURRING'
          : templateType) +
        '.xlsx'
      pom.click()
      document.body.removeChild(pom)
    }
  }, [bulkTemplate, templateType])

  useEffect((): void => {
    dispatch(getCountrys())
  }, [])

  useEffect((): void => {
    dispatch(createSetReceiptAttachmentIds([]))
  }, [screen])

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers(
        {
          q: filterInput,
          count: 10,
          page: page,
        },
        true,
      ),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  return (
    <>
      {!formStats.allLoaded ? (
        <LoadingBackgroundPage />
      ) : (
        <>
          <Helmet>
            <title>{`Create a Virtual Card | ${appName}`}</title>
            <meta
              name="description"
              content={`Create a Virtual Card page of ${appName}`}
            />
          </Helmet>
          {screen === CreateVirtualCardScreen.Pick && (
            <VirtualCardTypePicker
              setScreen={setScreen}
              userHasOrg={!!userOrganization}
              hasNoRCNs={activeRCNs === 0}
              noRCNsTitle={translate(
                'Register a company card to create virtual cards.',
              )}
              noRCNsBody={translate(
                "Virtual cards are created from registered company credit cards. Once a card is registered, you'll be able to create virtual cards.",
              )}
              standardDisabled={
                !userOrganization || formStats.standardCount === 0
              }
              decliningBalanceDisabled={
                !userOrganization || formStats.balanceLockCount === 0
              }
              recurringDisabled={
                !userOrganization || formStats.recurringCount === 0
              }
              hideRecurring={hideRecurringVCNOption}
              guestAccessDisabled={formStats.guestAccessCount === 0}
              issuerCycleDisabled={
                !userOrganization || formStats.issuerCycleCount === 0
              }
            />
          )}
          {screen === CreateVirtualCardScreen.Standard && (
            <CreateVirtualCardStandardContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formStats.standardCount === 0}
              labelCreditCardsAsAccounts={formStats.standardSourceCount > 0}
              creditCardOptions={formCreditCardStandardOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.standard.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              showSoftAlert={showSoftAlert}
            />
          )}
          {screen === CreateVirtualCardScreen.Recurring && (
            <CreateVirtualCardRecurringContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formStats.recurringCount === 0}
              labelCreditCardsAsAccounts={formStats.recurringSourceCount > 0}
              creditCardOptions={formCreditCardRecurringOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.recurring.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              showSoftAlert={showSoftAlert}
            />
          )}
          {screen === CreateVirtualCardScreen.DecliningBalance && (
            <CreateVirtualCardStandardContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formStats.balanceLockCount === 0}
              labelCreditCardsAsAccounts={formStats.balanceLockSourceCount > 0}
              creditCardOptions={formCreditCardBalanceLockOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.balanceLock.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              showSoftAlert={showSoftAlert}
              isBalanceLocked
            />
          )}
          {screen === CreateVirtualCardScreen.IssuerCycle && (
            <CreateVirtualCardIssuerCycleContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formCreditCards.issuerCycle.options.length === 0}
              labelCreditCardsAsAccounts
              creditCardOptions={formCreditCardIssuerCycleOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.issuerCycle.infiniteScrollProps
              }
              issuerCycleOptions={formCreditCards.issuerCycle.options}
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              showSoftAlert={showSoftAlert}
            />
          )}
          {screen === CreateVirtualCardScreen.GuestAccess && (
            <CreateVirtualCardGuestAccessContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formStats.guestAccessCount === 0}
              labelCreditCardsAsAccounts={formStats.guestAccessSourceCount > 0}
              creditCardOptions={formCreditCardGuestAccessOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.guestAccess.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              showSoftAlert={showSoftAlert}
            />
          )}
          {screen === CreateVirtualCardScreen.RecurringGuest && (
            <CreateVirtualCardRecurringContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formStats.recurringCount === 0}
              labelCreditCardsAsAccounts={formStats.recurringSourceCount > 0}
              creditCardOptions={formCreditCardGuestAccessOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.guestAccess.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              guestCard
              showSoftAlert={showSoftAlert}
            />
          )}
          {screen === CreateVirtualCardScreen.Multiple && (
            <CreateVirtualCardMultipleContent
              setScreen={setScreen}
              getCreditCardBulkTemplate={dispatchGetCreditCardBulkTemplate}
              postCreditCardBulkVirtualCards={
                dispatchPostCreditCardBulkVirtualCards
              }
              virtualCardPushTasks={virtualCardPushTasks}
              topLevelError={multipleTopLevelError}
              errorDetailMap={multipleErrorDetailMap}
              postingCreditCardBulkVirtualCards={
                postingCreditCardBulkVirtualCards
              }
              isFormDisabled={formStats.standardCount === 0}
              labelCreditCardsAsAccounts={formStats.standardSourceCount > 0}
              creditCardOptions={formCreditCardStandardOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.standard.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              userHasOrg={!!userOrganization}
              userHasActiveRCNs={!!activeRCNs}
              showSoftAlert={showSoftAlert}
            />
          )}
          {screen === CreateVirtualCardScreen.BillPay && (
            <CreateVirtualCardStandardContent
              receiptAttachments={receiptAttachments}
              uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
              postingVirtualCard={postingVirtualCard}
              topLevelError={topLevelError}
              errorDetailMap={errorDetailMap}
              uploadAttachments={dispatchUploadAttachments}
              deleteAttachment={deleteAttachment}
              postVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostVirtualCardAndOpenToastAndCloseFlyout
              }
              postBulkVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBulkVirtualCardAndOpenToastAndCloseFlyout
              }
              dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout={
                dispatchPostBillPayVirtualCardAndOpenToastAndCloseFlyout
              }
              setScreen={setScreen}
              userOptions={filteredUserEmailOptions}
              recentUserOptions={filteredRecentUserEmailOptions}
              userInfiniteScrollProps={userInfiniteScrollProps}
              isFormDisabled={formStats.billPayCount === 0}
              labelCreditCardsAsAccounts={formStats.billPaySourceCount > 0}
              creditCardOptions={formCreditCardStandardOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.standard.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              showSoftAlert={showSoftAlert}
              isBillPay
            />
          )}
          {screen === CreateVirtualCardScreen.MultipleBillPay && (
            <CreateVirtualCardMultipleContent
              setScreen={setScreen}
              getCreditCardBulkTemplate={dispatchGetCreditCardBulkTemplate}
              postCreditCardBulkVirtualCards={
                dispatchPostCreditCardBulkVirtualCards
              }
              virtualCardPushTasks={virtualCardPushTasks}
              topLevelError={multipleTopLevelError}
              errorDetailMap={multipleErrorDetailMap}
              postingCreditCardBulkVirtualCards={
                postingCreditCardBulkVirtualCards
              }
              isFormDisabled={formStats.billPayCount === 0}
              labelCreditCardsAsAccounts={formStats.billPaySourceCount > 0}
              creditCardOptions={formCreditCardStandardOptions}
              creditCardInfiniteScrollProps={
                formCreditCards.standard.infiniteScrollProps
              }
              pageLocation={handlePageLocation(calculatedBackgroundLocation)}
              userHasOrg={!!userOrganization}
              userHasActiveRCNs={!!activeRCNs}
              showSoftAlert={showSoftAlert}
              isBillPay
            />
          )}
        </>
      )}
    </>
  )
}

export default CreateVirtualCardForm
